import { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import AuthContext from "../context/AuthContext";
import useDecks from "../hooks/useDecks";
import useReviewQueue from "../hooks/useReviewQueue";
import { memoryApi } from "../services/api";
import { extractList } from "../hooks/utils";
import MemoryCard from "./MemoryCard";
import PageHeader from "./PageHeader";

const ReviewList = () => {
  const [memoriesToReview, setMemoriesToReview] = useState([]);
  const [currentMemoryToReview, setCurrentMemoryToReview] = useState(null);
  const [isActing, setIsActing] = useState(false);
  const [actionError, setActionError] = useState(null);
  // The deck filter lives in the URL, so a link can open Review on a deck.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDeck = searchParams.get("deck") || "";
  const setSelectedDeck = (value) =>
    setSearchParams(value ? { deck: value } : {}, { replace: true });
  const { isLoggedIn } = useContext(AuthContext);
  const { decks, loaded: decksLoaded, refreshDecks } = useDecks();
  // Fresh due counts on the chips: when Review opens, after each rating,
  // and after hiding a card (hidden cards aren't due).
  useEffect(() => {
    refreshDecks();
  }, [refreshDecks]);
  // A ?deck= for a deck that's gone (deleted since a link was made) or was
  // never a deck: say so and review everything, rather than show an
  // unfiltered-looking page that's secretly filtered to nothing.
  const [missingDeck, setMissingDeck] = useState(false);
  const deckExists = decks.some((d) => String(d.id) === selectedDeck);
  useEffect(() => {
    if (decksLoaded && selectedDeck && !deckExists) {
      setMissingDeck(true);
      setSearchParams({}, { replace: true });
    }
  }, [decksLoaded, selectedDeck, deckExists, setSearchParams]);
  const {
    queue,
    totalDue,
    loading: isLoading,
    refreshing,
    error: fetchError,
    reload,
  } = useReviewQueue(selectedDeck, isLoggedIn);
  const isProcessing = isActing || refreshing;
  // An empty queue means two different things (board #101): nothing due,
  // or no memories at all. Only then ask the library for its size. While
  // the answer is pending the page keeps its spinner — derived here, in
  // render, so the "all caught up" copy can't paint for a frame first. A
  // failed check settles as "unknown" and falls back to "all caught up".
  // A count above 0 is kept: the library can't empty itself mid-session,
  // so switching to an empty deck neither refetches nor flashes a spinner.
  const [library, setLibrary] = useState(null); // null | "unknown" | count
  const queueEmpty =
    !isLoading && !fetchError && queue.length === 0 && totalDue === 0;
  const checking = queueEmpty && library === null;
  useEffect(() => {
    if (!checking) return undefined;
    let live = true;
    memoryApi
      // one page (12) is plenty: only its count matters
      .getAll()
      .then((response) => {
        if (live) {
          setLibrary(response.data?.count ?? extractList(response.data).length);
        }
      })
      .catch(() => {
        if (live) setLibrary("unknown");
      });
    return () => {
      live = false;
    };
  }, [checking]);
  const noMemories = library === 0;
  const loadingView = isLoading || checking;
  const leftToReview = currentMemoryToReview ? memoriesToReview.length + 1 : 0;
  // Cards due but not loaded into this session (the action caps each batch).
  // Fixed at fetch time, so it stays accurate as the working queue drains.
  const moreDue = Math.max(totalDue - queue.length, 0);

  // Copy the fetched queue into local state; cards pop off as they're
  // reviewed, so the source list and the working list diverge on purpose.
  useEffect(() => {
    const [next, ...rest] = queue;
    setCurrentMemoryToReview(next ?? null);
    setMemoriesToReview(rest);
    setActionError(null);
  }, [queue]);

  function showNextMemory() {
    const [next, ...rest] = memoriesToReview;
    setCurrentMemoryToReview(next ?? null);
    setMemoriesToReview(rest);
  }

  async function handleRate(memoryId, rating) {
    if (isProcessing) return;
    setIsActing(true);
    try {
      await memoryApi.rate(memoryId, rating);
      setActionError(null);
      showNextMemory();
      refreshDecks();
    } catch {
      // Keep the current card on screen so the user can retry
      setActionError("Failed to save your rating. Please try again.");
    } finally {
      setIsActing(false);
    }
  }

  async function handleHide(memoryId) {
    if (isProcessing) return;
    setIsActing(true);
    try {
      await memoryApi.update(memoryId, { hidden_from_review: true });
      setActionError(null);
      showNextMemory();
      refreshDecks();
    } catch {
      setActionError("Failed to hide this card. Please try again.");
    } finally {
      setIsActing(false);
    }
  }

  function handleSkip() {
    if (isProcessing) return;
    setActionError(null);
    showNextMemory();
  }

  return (
    <div className="page-container mt-6 pt-4">
      <PageHeader
        title="Review Session"
        subtitle={
          loadingView
            ? "Looking for your brainfood..."
            : leftToReview > 0
              ? "Practice your flashcards with spaced repetition"
              : noMemories
                ? "Make a few flashcards, then practise them here"
                : "Great job! No memories to review right now"
        }
        badge={
          loadingView
            ? "Loading..."
            : leftToReview > 0
              ? leftToReview
              : noMemories
                ? null
                : "Complete"
        }
        badgeVariant={
          loadingView ? "secondary" : leftToReview > 0 ? "default" : "success"
        }
      />

      {missingDeck && (
        <Alert className="mb-3 py-2" role="status">
          <AlertDescription className="text-sm">
            That deck isn&apos;t there any more — reviewing all decks.
          </AlertDescription>
        </Alert>
      )}

      {/* Deck filter (board #23): real toggle buttons, each with what's
          due in it; the choice lives in the URL (?deck=) so the Decks
          page's "Review" link lands here filtered. */}
      {decks.length === 0 ? (
        <p className="mb-4 text-sm text-muted-foreground">
          Reviewing every card.{" "}
          <Link to="/study/decks" className="underline underline-offset-2">
            Make a deck
          </Link>{" "}
          to review one topic at a time.
        </p>
      ) : (
        <div
          className="mb-4 flex gap-2 overflow-auto pb-2"
          role="group"
          aria-label="Review which deck"
        >
          {[{ id: "", name: "All decks" }, ...decks].map((deck) => {
            const value = String(deck.id);
            const on = selectedDeck === value;
            return (
              <button
                key={value || "all"}
                type="button"
                aria-pressed={on}
                // The whole name, so it's "Spanish 3 due" everywhere
                // (inline pieces can lose their spaces when a name is
                // computed); visible text starts the same way.
                aria-label={
                  deck.id === ""
                    ? undefined
                    : `${deck.name} ${deck.due_count ?? 0} due`
                }
                disabled={isProcessing || isLoading}
                onClick={() => {
                  setMissingDeck(false);
                  setSelectedDeck(value);
                }}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-60",
                  on
                    ? "border-primary bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                )}
              >
                {deck.name}
                {deck.id !== "" && (
                  <>
                    {/* A real space and a spoken "due": the button reads
                        "Spanish 3 due", not "Spanish3". */}{" "}
                    <span
                      className={cn(
                        "rounded-full px-1.5 text-xs",
                        on ? "bg-primary-foreground/20" : "bg-background",
                      )}
                    >
                      {deck.due_count ?? 0}
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}

      {loadingView ? (
        <div className="mx-auto max-w-xl py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-primary" />
          <span className="sr-only">Loading...</span>
          <p className="mt-3 text-muted-foreground">
            Loading your review cards...
          </p>
        </div>
      ) : (
        <div
          className={cn(
            "transition-opacity duration-200",
            isProcessing && "opacity-50",
          )}
        >
          {fetchError && (
            <Alert variant="destructive" className="mb-3">
              <AlertDescription>
                Failed to load your review cards. Try switching decks or
                reloading the page.
              </AlertDescription>
            </Alert>
          )}
          {actionError && (
            <Alert variant="destructive" className="mb-3 pr-10">
              <AlertDescription>{actionError}</AlertDescription>
              <DismissButton onClick={() => setActionError(null)} />
            </Alert>
          )}
          {leftToReview === 0 && !currentMemoryToReview ? (
            <div className="mx-auto max-w-xl rounded-lg bg-muted p-12 text-center">
              {noMemories ? (
                <>
                  <h3 className="mb-3 text-xl font-semibold">
                    No memories yet
                  </h3>
                  <p className="mb-4 text-muted-foreground">
                    Memories are the flashcards you practise here. Create one
                    and it&apos;s ready to review straight away.
                  </p>
                  <Button asChild>
                    <Link to="/study/create">Create a memory</Link>
                  </Button>
                </>
              ) : moreDue > 0 ? (
                <>
                  <h3 className="mb-3 text-xl font-semibold text-success">
                    Session complete
                  </h3>
                  <p className="mb-3 text-muted-foreground">
                    You still have {moreDue} more card
                    {moreDue === 1 ? "" : "s"} due. Load the next batch to keep
                    going.
                  </p>
                  <Button onClick={reload} disabled={isProcessing}>
                    {refreshing ? "Loading…" : "Review more"}
                  </Button>
                </>
              ) : (
                <>
                  <h3 className="mb-3 text-xl font-semibold text-success">
                    🎉 All caught up!
                  </h3>
                  <p className="text-muted-foreground">
                    {selectedDeck
                      ? "Nothing due in this deck right now. Try All decks, or come back later."
                      : "You have reviewed all your memories for now. Come back later or create new memories to continue learning."}
                  </p>
                </>
              )}
            </div>
          ) : (
            currentMemoryToReview && (
              <MemoryCard
                key={currentMemoryToReview.id}
                memory={currentMemoryToReview}
                onMemoryHide={handleHide}
                onMemoryRate={handleRate}
                onMemorySkip={handleSkip}
                isProcessing={isProcessing}
              />
            )
          )}
        </div>
      )}
    </div>
  );
};

/** The X on a dismissible alert. */
function DismissButton({ onClick }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="absolute top-1.5 right-1.5 size-7"
      aria-label="Close"
      onClick={onClick}
    >
      <X />
    </Button>
  );
}

DismissButton.propTypes = { onClick: PropTypes.func.isRequired };

export default ReviewList;
